import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Row, Spinner } from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import { ToastContainer, toast } from "react-toastify";

import FinansIstatistikleri from "./walletStats";
import FinansGrafik from "./walletOverView";
import SonIslemler from "./walletActivities";

const BASE = "https://hiflowapi.stechomeyazilim.info";
const ACCEPTED = ["Kabul Edildi", "Onaylandı"];
const DELIVERED = ["TESLIM_ETME", "Tamamlandı"];

const SistemFinans = () => {
  document.title = "Sistem Finans Özeti | LojGo Admin";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const isAdmin = authUser?.Users_Type_ID === 3;

  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRevenue: 0,        // müşteri tarafı toplam ciro (Advert.Price toplamı)
    pendingDriverPayout: 0, // sürücülere ödenecek bakiye
    completedTrips: 0,
    monthlySeries: [],      // [{month, value}]
    growthPct: null,        // son ay / önceki ay
    walletHistory: [],
  });

  useEffect(() => {
    const fetchSystemStats = async () => {
      setIsLoading(true);
      try {
        if (!isAdmin) {
          toast.error("Bu sayfayı görüntüleme yetkiniz yok.");
          setIsLoading(false);
          return;
        }

        const [advRes, deliveriesRes, offersRes, paymentsRes] = await Promise.all([
          fetch(`${BASE}/Advert?$orderby=Created_DateTime desc&$top=5000`),
          fetch(`${BASE}/Advert_DeliveryStatus?$filter=Advert_DeliveryStatus_Status eq 'TESLIM_ETME' or Advert_DeliveryStatus_Status eq 'Tamamlandı'&$top=10000`),
          fetch(`${BASE}/Advert_Offers?$filter=Offer_Status eq 'Kabul Edildi' or Offer_Status eq 'Onaylandı'&$expand=Advert_Expenses&$top=10000`),
          fetch(`${BASE}/Users_Payment?$top=10000`),
        ]);

        const [advJ, delivJ, offerJ, payJ] = await Promise.all([
          advRes.json(), deliveriesRes.json(), offersRes.json(), paymentsRes.json(),
        ]);

        const adverts = advJ.value || [];
        const deliveries = delivJ.value || [];
        const offers = offerJ.value || [];
        const payments = payJ.value || [];

        // ─── Toplam ciro + aylık seri ───────────────────────────
        let totalRev = 0;
        const monthly = new Map(); // 'YYYY-MM' -> tutar

        adverts.forEach((a) => {
          const price = Number(a.Price) || 0;
          if (!price) return;
          totalRev += price;
          if (a.Created_DateTime) {
            const d = new Date(a.Created_DateTime);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
            monthly.set(key, (monthly.get(key) || 0) + price);
          }
        });

        // Son 12 ay
        const now = new Date();
        const monthlySeries = [];
        for (let i = 11; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
          monthlySeries.push({
            month: d.toLocaleString("tr-TR", { month: "short" }),
            value: monthly.get(key) || 0,
          });
        }

        // Büyüme: son ay / bir önceki ay
        const last = monthlySeries[monthlySeries.length - 1]?.value || 0;
        const prev = monthlySeries[monthlySeries.length - 2]?.value || 0;
        const growthPct = prev > 0 ? ((last - prev) / prev) * 100 : null;

        // ─── Sürücüye ödenecek bakiye ───────────────────────────
        const deliveredKey = new Set(
          deliveries.map((d) => `${d.Users_ID}|${d.Advert_ID}`)
        );
        let earned = 0;
        offers.forEach((o) => {
          if (!ACCEPTED.includes(o.Offer_Status)) return;
          if (!deliveredKey.has(`${o.Users_ID}|${o.Advert_ID}`)) return;
          earned += Number(o.Price) || 0;
          const exps = o.Advert_Expenses || [];
          if (exps.length > 0) {
            exps.forEach((e) => { if (e.Is_Approved) earned += Number(e.Total_Expense) || 0; });
          } else {
            earned += Number(o.Travel_Expenses) || 0;
          }
        });
        const paid = payments.reduce((s, p) => s + (Number(p.Price) || 0), 0);
        const pendingDriverPayout = Math.max(earned - paid, 0);

        // ─── Tamamlanan sefer ───────────────────────────────────
        const completedTrips = new Set(deliveries.map((d) => d.Advert_ID)).size;

        // ─── Son işlemler tablosu ───────────────────────────────
        const history = adverts.slice(0, 50)
          .filter((a) => Number(a.Price) > 0)
          .map((a) => ({
            idno: `LOJ-${a.ID}`,
            date: a.Created_DateTime,
            type: a.Advert_Status === "Tamamlandı" || a.Is_Active ? "Gelir" : "Bekleyen",
            user: a.Customer_Notes || "—",
            amount: Number(a.Price) || 0,
            status: a.Advert_Status || (a.Is_Active ? "Onaylandı" : "İşlemde"),
          }));

        setStats({
          totalRevenue: totalRev,
          pendingDriverPayout,
          completedTrips,
          monthlySeries,
          growthPct,
          walletHistory: history,
        });
      } catch (error) {
        console.error("Finans veri hatası:", error);
        toast.error("Finans verileri yüklenirken hata oluştu.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchSystemStats();
  }, [isAdmin]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Finans" breadcrumbItem="Sistem Özeti" />

          {isLoading ? (
            <div className="text-center py-5"><Spinner color="primary" /></div>
          ) : (
            <>
              <Row>
                <Col xl="4">
                  <FinansIstatistikleri stats={stats} user={authUser} growthPct={stats.growthPct} />
                </Col>

                <Col xl="8">
                  <Row>
                    <Col sm="4">
                      <Card className="mini-stats-wid shadow-sm border-0">
                        <CardBody>
                          <div className="d-flex">
                            <div className="me-3 align-self-center">
                              <i className="mdi mdi-cash-multiple h2 text-success mb-0" />
                            </div>
                            <div className="flex-grow-1">
                              <p className="text-muted mb-2">Toplam Sistem Cirosu</p>
                              <h5 className="mb-0">{stats.totalRevenue.toLocaleString("tr-TR")} TL</h5>
                              <small className="text-muted">Tüm zamanlar</small>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col sm="4">
                      <Card className="mini-stats-wid shadow-sm border-0">
                        <CardBody>
                          <div className="d-flex">
                            <div className="me-3 align-self-center">
                              <i className="mdi mdi-account-cash h2 text-danger mb-0" />
                            </div>
                            <div className="flex-grow-1">
                              <p className="text-muted mb-2">Sürücüye Ödenecek</p>
                              <h5 className="mb-0">{stats.pendingDriverPayout.toLocaleString("tr-TR")} TL</h5>
                              <small className="text-muted">Açık hakediş bakiyesi</small>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col sm="4">
                      <Card className="mini-stats-wid shadow-sm border-0">
                        <CardBody>
                          <div className="d-flex">
                            <div className="me-3 align-self-center">
                              <i className="mdi mdi-truck-check-outline h2 text-primary mb-0" />
                            </div>
                            <div className="flex-grow-1">
                              <p className="text-muted mb-2">Tamamlanan Sefer</p>
                              <h5 className="mb-0">{stats.completedTrips} Adet</h5>
                              <small className="text-muted">Teslim edilen ilan</small>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>

                  <FinansGrafik series={stats.monthlySeries} />
                </Col>
              </Row>

              <Row>
                <Col lg="12">
                  <SonIslemler history={stats.walletHistory} />
                </Col>
              </Row>
            </>
          )}
        </Container>
      </div>
      <ToastContainer autoClose={2000} position="bottom-right" />
    </React.Fragment>
  );
};

export default SistemFinans;
